/**
 * Service d'authentification Google avec Firebase Auth
 * Utilise Firebase Auth pour la connexion (gère le paramètre state)
 * Utilise GAPI pour les APIs Google (Calendar, Drive)
 */

import {
  signInWithPopup,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { clearAllUserData } from './userDataService';

const STORAGE_KEY = 'adhd-planner-google-auth';
const TOKEN_EXPIRY_KEY = 'google_token_expires_at';

// Scopes requis pour l'application
export const GOOGLE_SCOPES = {
  PROFILE: 'https://www.googleapis.com/auth/userinfo.profile',
  EMAIL: 'https://www.googleapis.com/auth/userinfo.email',
  CALENDAR: 'https://www.googleapis.com/auth/calendar.events',
  DRIVE_FILE: 'https://www.googleapis.com/auth/drive.file',
  DRIVE_APPDATA: 'https://www.googleapis.com/auth/drive.appdata',
  TASKS: 'https://www.googleapis.com/auth/tasks',
  PHOTOS_APPEND: 'https://www.googleapis.com/auth/photoslibrary.appendonly',
};

class GoogleAuthService {
  constructor() {
    this.gapiInited = false;
    this.isSignedIn = false;
    this.user = null;
    this.accessToken = null;
    this.onAuthChange = null;
    this.onTokenExpiry = null; // Callback déclenché quand le token expire
    this.initPromise = null;
    this.unsubscribeAuth = null;
    // Track which APIs are available
    this.availableApis = {
      calendar: false,
      drive: false,
      tasks: false,
    };
  }

  // Sauvegarder les données d'auth
  saveAuthData(token, user) {
    try {
      const expiresAt = Date.now() + 3600 * 1000; // 1 heure
      const data = {
        token: {
          access_token: token,
          savedAt: Date.now(),
          expires_in: 3600
        },
        user,
        savedAt: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
    } catch (e) {
      console.warn('Erreur sauvegarde auth Google:', e);
    }
  }

  // Récupérer les données d'auth sauvegardées
  getSavedAuthData() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return null;

      const parsed = JSON.parse(data);
      const token = parsed.token;

      // Vérifier expiration (1 heure par défaut)
      const expiresIn = token.expires_in || 3600;
      const expiresAt = token.savedAt + (expiresIn * 1000);

      if (Date.now() >= expiresAt - 60000) {
        this.clearAuthData();
        return null;
      }

      return parsed;
    } catch (e) {
      console.warn('Erreur lecture auth Google:', e);
      return null;
    }
  }

  // Effacer les données d'auth
  clearAuthData() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  // Lire le timestamp d'expiration du token
  getTokenExpiresAt() {
    try {
      const val = localStorage.getItem(TOKEN_EXPIRY_KEY);
      return val ? parseInt(val, 10) : null;
    } catch {
      return null;
    }
  }

  /**
   * Retourne l'état actuel du token Google OAuth.
   * @returns {'none'|'connected'|'expiring'|'expired'}
   *   none      → non connecté
   *   connected → token valide
   *   expiring  → expire dans moins de 5 minutes
   *   expired   → déjà expiré
   */
  getTokenStatus() {
    if (!this.isSignedIn) return 'none';
    const expiresAt = this.getTokenExpiresAt();
    if (!expiresAt) return 'none';
    const now = Date.now();
    const remaining = expiresAt - now;
    if (remaining <= 0) return 'expired';
    if (remaining < 5 * 60 * 1000) return 'expiring';
    return 'connected';
  }

  /**
   * Enregistre un callback appelé quand le token est expiré.
   * @param {function} cb
   */
  setOnTokenExpiry(cb) {
    this.onTokenExpiry = cb;
  }

  /**
   * Wrapper à utiliser avant tout appel GAPI critique.
   * - Vérifie l'état du token
   * - En cas d'expiration imminente, tente un refresh Firebase silencieux
   * - Intercepte les 401 en cours d'appel et notifie via onTokenExpiry
   * - Ne bloque jamais : retourne null si le token est expiré
   *
   * @param {function} apiCallFn - Fonction async à exécuter si le token est valide
   * @returns {*} Résultat de apiCallFn, ou null si token expiré
   */
  async withTokenCheck(apiCallFn) {
    const status = this.getTokenStatus();

    if (status === 'expired') {
      console.warn('withTokenCheck: token expiré, appel annulé');
      this.onTokenExpiry?.();
      return null;
    }

    if (status === 'expiring') {
      // Tentative de refresh silencieux via Firebase
      // (renouvelle le Firebase ID token, pas l'OAuth access token,
      //  mais c'est la meilleure option sans interaction utilisateur)
      try {
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
          console.log('withTokenCheck: refresh Firebase réussi');
        }
      } catch (e) {
        console.warn('withTokenCheck: refresh silencieux échoué', e);
        // On tente quand même l'appel, un 401 le stoppera proprement
      }
    }

    try {
      return await apiCallFn();
    } catch (error) {
      const errStatus = error?.status || error?.result?.error?.code;
      if (errStatus === 401) {
        console.warn('withTokenCheck: 401 intercepté, token invalide');
        this.onTokenExpiry?.();
      }
      throw error;
    }
  }

  // Initialiser les APIs Google
  async init(onAuthChange) {
    if (this.initPromise) return this.initPromise;

    this.onAuthChange = onAuthChange;

    this.initPromise = new Promise(async (resolve, reject) => {
      // Vérifier d'abord s'il y a un résultat de redirect en attente (AWAIT obligatoire)
      await this.checkRedirectResult();

      // Écouter les changements d'état Firebase Auth
      this.unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('👤 onAuthStateChanged:', firebaseUser ? firebaseUser.email : 'null');
        if (firebaseUser) {
          // Utilisateur connecté via Firebase
          this.user = {
            id: firebaseUser.uid,
            name: firebaseUser.displayName,
            email: firebaseUser.email,
            picture: firebaseUser.photoURL,
          };
          this.isSignedIn = true;

          // Récupérer le token d'accès
          if (!this.accessToken) {
            const savedAuth = this.getSavedAuthData();
            if (savedAuth?.token?.access_token) {
              this.accessToken = savedAuth.token.access_token;
            }
          }

          // Configurer GAPI avec le token si disponible
          if (this.accessToken) {
            const setGapiToken = () => {
              if (window.gapi?.client?.setToken) {
                window.gapi.client.setToken({ access_token: this.accessToken });
              }
            };
            setGapiToken();
            setTimeout(setGapiToken, 1000);
          } else {
            // Token manquant — sera demande au prochain clic utilisateur
            console.log('ℹ️ Pas de token OAuth — sera demande a la prochaine action Google');
          }

          console.log('Session Firebase restaurée:', this.user?.name);
          if (this.onAuthChange) {
            this.onAuthChange(true, this.user);
          }
          // Notify other components that auth is ready (profile reload etc.)
          window.dispatchEvent(new Event('firebase-auth-ready'));
        } else {
          this.user = null;
          this.isSignedIn = false;
          this.accessToken = null;
          if (this.onAuthChange) {
            this.onAuthChange(false, null);
          }
        }
      });

      // Initialiser GAPI pour les APIs Google (Calendar, Drive, Tasks)
      // Attendre un court délai pour que gapi soit chargé depuis le script HTML
      setTimeout(() => {
        this.initGapi(resolve, reject);
      }, 500);
    });

    return this.initPromise;
  }

  // Vérifier s'il y a un résultat de redirect en attente
  async checkRedirectResult() {
    console.log('🔍 Vérification du résultat redirect...');
    try {
      const result = await getRedirectResult(auth);
      console.log('🔍 Résultat redirect:', result ? 'Trouvé' : 'Aucun');
      if (result) {
        const credential = GoogleAuthProvider.credentialFromResult(result);

        this.user = {
          id: result.user.uid,
          name: result.user.displayName,
          email: result.user.email,
          picture: result.user.photoURL,
        };
        this.isSignedIn = true;

        if (credential?.accessToken) {
          this.accessToken = credential.accessToken;
          this.saveAuthData(this.accessToken, this.user);
          console.log('🔑 Token OAuth sauvegardé depuis redirect');
        }

        // Configurer GAPI si disponible (peut être chargé plus tard via initGapi)
        if (this.accessToken && window.gapi?.client?.setToken) {
          window.gapi.client.setToken({ access_token: this.accessToken });
        }

        if (this.onAuthChange) {
          this.onAuthChange(true, this.user);
        }
        console.log('✅ Connexion par redirect réussie:', this.user.email);
      }
    } catch (error) {
      // auth/popup-blocked or similar — not a real error
      if (error?.code !== 'auth/popup-blocked') {
        console.warn('Erreur vérification redirect:', error);
      }
    }
  }

  async initGapi(resolve, reject) {
    // Vérifier que gapi est disponible
    if (!window.gapi || typeof window.gapi.load !== 'function') {
      console.warn('⚠️ GAPI non disponible ou incomplet');
      this.gapiInited = false;
      resolve();
      return;
    }

    console.log('📦 Chargement du client GAPI...');

    // Wrapper global pour capturer toutes les erreurs
    const loadClient = () => {
      return new Promise((res) => {
        try {
          // Essayer de charger le client
          window.gapi.load('client', {
            callback: () => {
              console.log('✅ Client GAPI callback appelé');
              res(true);
            },
            onerror: (err) => {
              console.warn('⚠️ Erreur callback GAPI:', err);
              res(false);
            },
            timeout: 10000,
            ontimeout: () => {
              console.warn('⚠️ Timeout GAPI');
              res(false);
            }
          });
        } catch (e) {
          console.warn('⚠️ Exception gapi.load:', e.message);
          res(false);
        }
      });
    };

    try {
      const loaded = await loadClient();

      if (!loaded || !window.gapi.client) {
        console.warn('⚠️ Client GAPI non chargé, continuer sans APIs Google');
        this.gapiInited = false;
        resolve();
        return;
      }

      // Configurer l'API key — utiliser la même clé que Firebase pour éviter le conflit de projets
      const apiKey = process.env.REACT_APP_FIREBASE_API_KEY || process.env.REACT_APP_GOOGLE_API_KEY;
      if (apiKey && window.gapi.client.setApiKey) {
        window.gapi.client.setApiKey(apiKey);
        console.log('✅ API Key configurée');
      }

      // Charger les APIs
      const apis = [
        { name: 'calendar', discovery: 'calendar', version: 'v3' },
        { name: 'drive', discovery: 'drive', version: 'v3' },
        { name: 'tasks', discovery: 'tasks', version: 'v1' },
      ];

      for (const api of apis) {
        try {
          await window.gapi.client.load(api.discovery, api.version);
          this.availableApis[api.name] = true;
          console.log(`✅ API ${api.name} chargée`);
        } catch (err) {
          console.warn(`⚠️ API ${api.name} non disponible:`, err?.message || err);
          this.availableApis[api.name] = false;
        }
      }

      this.gapiInited = true;

      // Restaurer le token si existant
      const savedAuth = this.getSavedAuthData();
      if (savedAuth?.token?.access_token && window.gapi.client.setToken) {
        window.gapi.client.setToken({ access_token: savedAuth.token.access_token });
        this.accessToken = savedAuth.token.access_token;
        console.log('✅ Token restauré depuis localStorage');
      }

      resolve();
    } catch (error) {
      console.error('Erreur init GAPI:', error);
      this.gapiInited = false;
      resolve(); // Continuer sans GAPI
    }
  }

  // Check if a specific API is available
  isApiAvailable(apiName) {
    return this.availableApis[apiName] === true;
  }

  // Se connecter avec Google via Firebase Auth
  async signIn() {
    console.log('🔐 Début signIn...');

    // Strategy: try popup first (gets OAuth token directly),
    // fallback to redirect if popup blocked
    return new Promise((resolve, reject) => {
      let settled = false;

      const timeout = setTimeout(() => {
        if (!settled) { settled = true; reject(new Error('Connexion expirée')); }
      }, 120000);

      // Listen for auth state change (catches result even if popup COOP blocks)
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!firebaseUser || settled) return;
        settled = true;
        clearTimeout(timeout);
        unsubscribe();

        this.user = {
          id: firebaseUser.uid,
          name: firebaseUser.displayName,
          email: firebaseUser.email,
          picture: firebaseUser.photoURL,
        };
        this.isSignedIn = true;

        // Try to get token from saved data
        if (!this.accessToken) {
          const saved = this.getSavedAuthData();
          if (saved?.token?.access_token) this.accessToken = saved.token.access_token;
        }
        // Note: if no token, user must click again — GIS popup requires user gesture

        if (this.accessToken && window.gapi?.client?.setToken) {
          window.gapi.client.setToken({ access_token: this.accessToken });
        }

        if (this.onAuthChange) this.onAuthChange(true, this.user);
        resolve(this.user);
      });

      // Try popup
      signInWithPopup(auth, googleProvider)
        .then((result) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          unsubscribe();

          const credential = GoogleAuthProvider.credentialFromResult(result);
          this.accessToken = credential?.accessToken;

          this.user = {
            id: result.user.uid,
            name: result.user.displayName,
            email: result.user.email,
            picture: result.user.photoURL,
          };
          this.isSignedIn = true;

          if (this.accessToken) {
            this.saveAuthData(this.accessToken, this.user);
            if (window.gapi?.client?.setToken) {
              window.gapi.client.setToken({ access_token: this.accessToken });
            }
          }

          if (this.onAuthChange) this.onAuthChange(true, this.user);
          resolve(this.user);
        })
        .catch((error) => {
          if (error.code === 'auth/popup-closed-by-user' ||
              error.code === 'auth/cancelled-popup-request') {
            if (!settled) { settled = true; clearTimeout(timeout); unsubscribe(); resolve(null); }
            return;
          }
          // Popup failed (COOP etc.) — onAuthStateChanged will catch it
          console.warn('Popup error (fallback to onAuthStateChanged):', error.code);
        });
    });
  }

  /**
   * Request a fresh OAuth access token using Google Identity Services.
   * Uses redirect flow if popup is blocked.
   */
  async requestFreshToken() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google?.accounts?.oauth2) {
      console.warn('GIS not available for token refresh');
      return;
    }

    return new Promise((resolve) => {
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: Object.values(GOOGLE_SCOPES).join(' '),
          callback: (response) => {
            if (response.error) {
              console.warn('GIS token error:', response.error);
              resolve();
              return;
            }
            this.accessToken = response.access_token;
            this.saveAuthData(this.accessToken, this.user);
            if (window.gapi?.client?.setToken) {
              window.gapi.client.setToken({ access_token: this.accessToken });
            }
            console.log('🔑 Fresh OAuth token obtained via GIS');
            resolve();
          },
          error_callback: (err) => {
            console.warn('GIS popup blocked, skipping token refresh:', err?.type);
            resolve();
          },
        });

        // Request token silently (prompt: '' = no UI if already consented)
        tokenClient.requestAccessToken({ prompt: '' });
      } catch (err) {
        console.warn('GIS requestFreshToken failed:', err);
        resolve();
      }
    });
  }

  // Se déconnecter
  async signOut() {
    try {
      // Vider toutes les données utilisateur AVANT la déconnexion Firebase
      clearAllUserData();

      // Déconnexion Firebase
      await firebaseSignOut(auth);

      // Révoquer le token GAPI si disponible
      if (this.accessToken) {
        try {
          await fetch(`https://oauth2.googleapis.com/revoke?token=${this.accessToken}`, {
            method: 'POST',
          });
        } catch (e) {
          console.warn('Erreur révocation token:', e);
        }
      }

      // Nettoyer GAPI
      if (window.gapi?.client) {
        window.gapi.client.setToken(null);
      }

      this.isSignedIn = false;
      this.user = null;
      this.accessToken = null;

      if (this.onAuthChange) {
        this.onAuthChange(false, null);
      }

      console.log('Déconnexion réussie');
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      throw error;
    }
  }

  // Rafraîchir le token (reconnecter si expiré)
  async refreshToken() {
    if (!auth.currentUser) {
      return null;
    }

    try {
      // Force refresh du token Firebase
      const idToken = await auth.currentUser.getIdToken(true);

      // Note: Pour obtenir un nouvel access token OAuth,
      // l'utilisateur doit se reconnecter
      // Firebase Auth ne stocke pas le refresh token OAuth

      return idToken;
    } catch (error) {
      console.error('Erreur refresh token:', error);
      return null;
    }
  }

  // Vérifier si connecté
  checkSignedIn() {
    return this.isSignedIn && this.user !== null;
  }

  // Obtenir l'utilisateur actuel
  getUser() {
    return this.user;
  }

  // Obtenir le token d'accès actuel
  getAccessToken() {
    return this.accessToken;
  }

  // Nettoyer les listeners
  destroy() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }
}

// Singleton
const googleAuthService = new GoogleAuthService();
export default googleAuthService;
